import React from 'react';
import { useFormik } from 'formik';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import * as yup from 'yup';
import { RiMailLine, RiLockPasswordLine } from 'react-icons/ri';
import Typography from '@mui/material/Typography';
import { ButtonRow, FormInput, Loader, AuthLayout, Message } from '@omnigenbiodata/ui';

import { useTranslation } from 'react-i18next';
import { useAuth } from '@omnigenbiodata/ui';
import { MESSAGES } from '../../../../core/constants/forms.constants';
import { AUTH_CHALLENGES } from '../../../../core/constants/aws.constants';
import ROUTES from '../../../../core/constants/routes.constants';

const validationSchema = yup.object().shape({
  email: yup.string().email(MESSAGES.emailInvalid).required(MESSAGES.emailRequired),
  password: yup.string().required(MESSAGES.passwordRequired),
});

function Login() {
  const { t } = useTranslation('portal');
  const navigateTo = useNavigate();
  const { hasError, isBusy, forgotPasswordHasSuccess, signIn, challengeName } = useAuth();

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema,
    onSubmit: (values) => {
      signIn(values.email, values.password);
    },
  });

  const handleCancel = () => {
    navigateTo(ROUTES.root);
  };

  if (
    !isBusy &&
    (challengeName === AUTH_CHALLENGES.MFA_SETUP || challengeName === AUTH_CHALLENGES.SOFTWARE_TOKEN_MFA)
  ) {
    return <Navigate to={ROUTES.authMultiFactor} />;
  }

  if (
    !isBusy &&
    (challengeName === AUTH_CHALLENGES.NEW_PASSWORD || challengeName === AUTH_CHALLENGES.NEW_PASSWORD_REQUIRED)
  ) {
    return <Navigate to={ROUTES.authNewPassword} />;
  }

  return (
    <AuthLayout
      title={t('signin.title')}
      text={t('signin.text')}
      hasError={hasError}
      dmsa
      errorTitle={t('alerts.loginFailed.title')}
      errorText={t('alerts.loginFailed.text')}
    >
      <form onSubmit={formik.handleSubmit}>
        {forgotPasswordHasSuccess && <Message>{t('alerts.resetPasswordSuccess')}</Message>}

        <Box mb={8}>
          <FormInput
            error={formik.errors.email}
            name="email"
            label={t('forms.dmEmail')}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.email}
            value={formik.values.email}
            startAdornment={<RiMailLine fontSize="large" />}
          />
          <FormInput
            error={formik.errors.password}
            name="password"
            label={t('forms.password')}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.password}
            value={formik.values.password}
            type="password"
            autocomplete="new-password"
            startAdornment={<RiLockPasswordLine fontSize="large" />}
          />
          <Link to={`${ROUTES.authResetPassword}`}>
            <Typography paragraph variant="body1" align="right" color="textPrimary">
              {t('buttons.forgotPassword')}
            </Typography>
          </Link>
        </Box>
        <ButtonRow forwardLabel={t('buttons.signIn')} onBack={handleCancel} buttonSize="large" forwardColor="primary" />
      </form>
      <Loader isVisible={isBusy} label={t('loaders.auth') as string} />
    </AuthLayout>
  );
}

export default Login;
